import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Section, Heading, TrialCTA } from "./Home";
import { CONSULTANT_BENEFITS } from "../../components/site/content";
import { useSEO } from "../../lib/api";

export function ConsultantBenefits() {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {CONSULTANT_BENEFITS.map((b) => (
        <div key={b.title} className="lift border border-white/10 rounded-xl p-6 bg-white/[0.04]" data-testid={`consultant-benefit-${b.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}`}>
          <b.icon className="w-6 h-6 text-sky-400" />
          <h3 className="mt-4 font-semibold text-white">{b.title}</h3>
          <p className="mt-2 text-sm text-slate-400 leading-relaxed">{b.text}</p>
        </div>
      ))}
    </div>
  );
}

export function ConsultantHomeSection() {
  return (
    <Section id="consultants" className="bg-[#0A192F]" testid="home-consultants">
      <div className="grid lg:grid-cols-12 gap-10 items-center mb-12">
        <div className="lg:col-span-7">
          <p className="eyebrow !text-sky-400">For Quality Consultants &amp; Auditors</p>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">Manage Your Clients' Quality Systems. From Anywhere.</h2>
          <p className="mt-4 text-slate-300 leading-relaxed">Whether you're an independent Quality Consultant, ISO Auditor or Management Systems Specialist, QORDEL 360 gives you the flexibility to support your clients remotely, maintain their management systems and collaborate with their teams — wherever you are in the world.</p>
          <Link to="/consultants" className="mt-7 inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-white font-semibold px-6 py-3.5 rounded-md transition-colors" data-testid="home-consultant-cta">Are You a Quality Consultant? Discover QORDEL 360 <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="lg:col-span-5 rounded-xl bg-white/5 border border-white/10 p-2">
          <img src="/screens/consultant-switcher.png" alt="QORDEL 360 consultant client switcher" className="w-full rounded-lg" data-testid="home-consultant-screenshot" />
          <p className="px-2 pt-2 pb-1 text-xs text-slate-400">Consultant login: switch between authorised client organisations.</p>
        </div>
      </div>
      <ConsultantBenefits />
    </Section>
  );
}

const STEPS = [
  ["Recommend QORDEL 360", "Your client starts a free trial and subscribes in their own name. Their data, subscription and account always belong to them."],
  ["Your client authorises you", "From Users & Licences, their administrator enters your email. You receive an invitation and set up one consultant login."],
  ["Support every client from one login", "Switch between each client who has authorised you. Work on their manual, registers, audits, NCRs and corrective actions."],
  ["Your client stays in control", "You use one of their user licences, you can't change their billing or users, every action is in their audit log and they can remove access at any time."],
];

export default function Consultants() {
  useSEO("For Quality Consultants & ISO Auditors", "QORDEL 360 lets ISO consultants and quality auditors support multiple client management systems remotely, securely and from one login.");
  return (
    <>
      <section className="bg-[#0A192F] text-white" data-testid="consultants-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <p className="eyebrow !text-sky-400">For ISO Consultants, Quality Auditors &amp; Management Systems Specialists</p>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08]">Manage Your Clients' Quality Systems. From Anywhere.</h1>
            <p className="mt-6 text-slate-300 leading-relaxed">Support your clients remotely, maintain their management systems and collaborate with their teams — wherever you are in the world.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register" className="inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-400 text-white font-semibold px-6 py-3.5 rounded-md" data-testid="consultants-trial-btn">Try It With a Free Trial <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/contact" className="inline-flex items-center justify-center border border-white/25 hover:bg-white/10 font-semibold px-6 py-3.5 rounded-md" data-testid="consultants-contact-btn">Talk to us about partnering</Link>
            </div>
          </div>
          <div className="lg:col-span-6 rounded-xl bg-white/5 border border-white/10 p-2"><img src="/screens/ims-manual.png" alt="QORDEL 360 IMS Manual" className="w-full rounded-lg" /></div>
        </div>
      </section>
      <Section className="bg-[#0A192F] border-t border-white/10" testid="consultants-benefits">
        <Heading eyebrow="Key benefits" title="Built to support the work you already do." light />
        <ConsultantBenefits />
      </Section>
      <Section testid="consultants-better-way">
        <Heading eyebrow="Recommend with confidence" title="A Better Way to Support Your Clients." text="QORDEL 360 doesn't replace your expertise — it gives you a professional platform to deliver it. Recommend it to your clients, and keep providing your consulting, auditing and system maintenance services through it." />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map(([t, d], i) => (
            <div key={t} className="border border-slate-200 rounded-xl p-6 bg-white">
              <span className="font-mono text-xs text-sky-700">STEP {i + 1}</span>
              <h3 className="mt-2 font-semibold text-slate-900">{t}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </Section>
      <Section className="bg-slate-50 border-y border-slate-200" testid="consultants-what">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <img src="/screens/standards.png" alt="QORDEL 360 Standards & Clause Library" className="rounded-xl border border-slate-200 w-full" />
          <div>
            <Heading eyebrow="What you can do for clients" title="Everything an IMS needs, in one place." />
            <ul className="space-y-2.5">
              {["Maintain the IMS / Quality Manual and policies", "Plan and run internal audits from clause-based templates", "Raise and close NCRs and corrective actions", "Keep document control, legal requirements and risk registers current", "Prepare management reviews and track objectives & KPIs", "Help with transitions to new ISO versions using the Standards Library"].map((t) => (
                <li key={t} className="flex gap-2 text-slate-700"><Check className="w-5 h-5 text-emerald-600 shrink-0" />{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </Section>
      <TrialCTA />
    </>
  );
}
